import { C, mono, sans } from "../theme.ts";

// A small markdown renderer, written here rather than pulled in.
//
// The corpus is known: the roadmap, the audits and the session briefs. They use
// headings, paragraphs, lists, tables, fenced code, blockquotes and rules, and
// nothing else. A library would be a dependency, a bundle and a supply chain
// for that.
//
// It builds React elements and never sets innerHTML, so a document containing
// a <script> tag renders it as the four words it is.

type Inline = string | JSX.Element;

// Ordered: the first alternative that matches at a position wins, so `code`
// is tried before emphasis and a backticked **word** stays literal.
const INLINE = /(`[^`]+`)|(\[[^\]]+\]\([^)\s]+\))|(\*\*[^*]+\*\*)|(\*[^*]+\*)|(_[^_]+_)/g;

/** Exported for the one place that renders a single line of a document inside its own layout: the Workout view's rows. */
export function inline(text: string, keyBase: string): Inline[] {
  const out: Inline[] = [];
  let last = 0;
  let n = 0;
  for (const m of text.matchAll(INLINE)) {
    const at = m.index ?? 0;
    if (at > last) out.push(text.slice(last, at));
    const token = m[0];
    const key = `${keyBase}-${n++}`;
    if (token.startsWith("`")) {
      out.push(
        <code key={key} style={{
          fontFamily: mono, fontSize: "0.88em", background: C.flagBg,
          padding: "1px 5px", borderRadius: 5, wordBreak: "break-word",
        }}>{token.slice(1, -1)}</code>,
      );
    } else if (token.startsWith("[")) {
      const split = token.indexOf("](");
      const label = token.slice(1, split);
      const href = token.slice(split + 2, -1);
      // Anything not plainly http(s) is shown as text: a doc is data, and a
      // link in it should not be able to name a scheme the browser will run.
      out.push(
        /^https?:\/\//i.test(href)
          ? <a key={key} href={href} target="_blank" rel="noreferrer noopener"
               style={{ color: C.ink, textUnderlineOffset: 3 }}>{label}</a>
          : <span key={key}>{label}</span>,
      );
    } else if (token.startsWith("**")) {
      out.push(<strong key={key} style={{ fontWeight: 600 }}>{token.slice(2, -2)}</strong>);
    } else {
      out.push(<em key={key}>{token.slice(1, -1)}</em>);
    }
    last = at + token.length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

const cells = (row: string) =>
  row.replace(/^\s*\|/, "").replace(/\|\s*$/, "").split("|").map((c) => c.trim());

const isDivider = (row: string) => /^\s*\|?[\s:|-]+\|[\s:|-]*$/.test(row) && row.includes("-");

export function Markdown({ text }: { text: string }) {
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const out: JSX.Element[] = [];
  let i = 0;
  let key = 0;
  const k = () => `b${key++}`;

  const para = (buf: string[]) => {
    if (!buf.length) return;
    const joined = buf.join(" ").trim();
    if (joined) {
      out.push(
        <p key={k()} style={{ fontSize: 14, lineHeight: 1.65, color: C.inkSoft, margin: "0 0 12px" }}>
          {inline(joined, k())}
        </p>,
      );
    }
    buf.length = 0;
  };

  const buffer: string[] = [];

  while (i < lines.length) {
    const line = lines[i];

    if (!line.trim()) { para(buffer); i++; continue; }

    // Fenced code. Everything inside is literal, including things that look
    // like headings or table rows.
    if (/^\s*```/.test(line)) {
      para(buffer);
      const body: string[] = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i])) { body.push(lines[i]); i++; }
      i++;
      out.push(
        <pre key={k()} style={{
          fontFamily: mono, fontSize: 12, lineHeight: 1.55, background: C.card,
          border: `1px solid ${C.rule}`, borderRadius: 10, padding: "12px 14px",
          overflowX: "auto", margin: "0 0 14px", color: C.inkSoft,
        }}>{body.join("\n")}</pre>,
      );
      continue;
    }

    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      para(buffer);
      const depth = heading[1].length;
      const size = depth === 1 ? 20 : depth === 2 ? 17 : 15;
      out.push(
        <p key={k()} style={{
          fontSize: size, fontWeight: depth <= 2 ? 600 : 500, color: C.ink,
          lineHeight: 1.35, margin: out.length ? "20px 0 8px" : "0 0 8px",
        }}>{inline(heading[2], k())}</p>,
      );
      i++;
      continue;
    }

    if (/^\s*(---+|\*\*\*+|___+)\s*$/.test(line)) {
      para(buffer);
      out.push(<hr key={k()} style={{ border: 0, borderTop: `1px solid ${C.rule}`, margin: "18px 0" }} />);
      i++;
      continue;
    }

    // A table: a row of pipes followed by a divider row.
    if (line.includes("|") && i + 1 < lines.length && isDivider(lines[i + 1])) {
      para(buffer);
      const head = cells(line);
      i += 2;
      const body: string[][] = [];
      while (i < lines.length && lines[i].includes("|") && lines[i].trim()) {
        body.push(cells(lines[i]));
        i++;
      }
      const cell: React.CSSProperties = {
        padding: "7px 10px", borderBottom: `1px solid ${C.rule}`,
        fontSize: 13, lineHeight: 1.5, textAlign: "left", verticalAlign: "top",
      };
      out.push(
        // The corpus has wide tables and the page must never scroll sideways,
        // so the table scrolls inside its own box.
        <div key={k()} style={{ overflowX: "auto", margin: "0 0 14px" }}>
          <table style={{ borderCollapse: "collapse", minWidth: "100%", fontFamily: sans }}>
            <thead>
              <tr>{head.map((c, j) => (
                <th key={j} style={{ ...cell, color: C.inkFaint, fontFamily: mono, fontSize: 11, letterSpacing: 0.6, fontWeight: 400, whiteSpace: "nowrap" }}>
                  {inline(c, k())}
                </th>
              ))}</tr>
            </thead>
            <tbody>{body.map((row, r) => (
              <tr key={r}>{row.map((c, j) => (
                <td key={j} style={{ ...cell, color: C.inkSoft }}>{inline(c, k())}</td>
              ))}</tr>
            ))}</tbody>
          </table>
        </div>,
      );
      continue;
    }

    if (/^\s*>\s?/.test(line)) {
      para(buffer);
      const body: string[] = [];
      while (i < lines.length && /^\s*>\s?/.test(lines[i])) {
        body.push(lines[i].replace(/^\s*>\s?/, ""));
        i++;
      }
      // A blank `>` line inside a quote is a paragraph break and is kept as
      // one. Joining the whole quote into a single run turned the eating
      // fallbacks — four routes, each several sentences — into one wall, and
      // that line is read while hungry or on a freeze day.
      const paras: string[][] = [[]];
      for (const b of body) {
        if (b.trim()) paras[paras.length - 1].push(b);
        else if (paras[paras.length - 1].length) paras.push([]);
      }
      const written = paras.filter((g) => g.length);
      out.push(
        <blockquote key={k()} style={{
          margin: "0 0 14px", padding: "2px 0 2px 14px", borderLeft: `3px solid ${C.ruleStrong}`,
          fontSize: 14, lineHeight: 1.6, color: C.inkFaint,
        }}>{written.map((g, j) => (
          <p key={j} style={{ margin: j === written.length - 1 ? 0 : "0 0 10px" }}>
            {inline(g.join(" "), k())}
          </p>
        ))}</blockquote>,
      );
      continue;
    }

    const bullet = /^\s*([-*+]|\d+\.)\s+/.exec(line);
    if (bullet) {
      para(buffer);
      const ordered = /\d/.test(bullet[1]);
      const items: string[] = [];
      while (i < lines.length) {
        const m = /^\s*([-*+]|\d+\.)\s+(.*)$/.exec(lines[i]);
        if (m) { items.push(m[2]); i++; continue; }
        // A wrapped continuation line belongs to the item above it.
        if (items.length && /^\s+\S/.test(lines[i])) { items[items.length - 1] += ` ${lines[i].trim()}`; i++; continue; }
        break;
      }
      const style: React.CSSProperties = { margin: "0 0 12px", paddingLeft: 20 };
      const li = items.map((it, j) => (
        <li key={j} style={{ fontSize: 14, lineHeight: 1.6, color: C.inkSoft, marginBottom: 5 }}>
          {inline(it, k())}
        </li>
      ));
      out.push(ordered ? <ol key={k()} style={style}>{li}</ol> : <ul key={k()} style={style}>{li}</ul>);
      continue;
    }

    buffer.push(line);
    i++;
  }
  para(buffer);

  return <div style={{ fontFamily: sans }}>{out}</div>;
}
